import { useForm, Controller } from "react-hook-form";
import {  z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import * as translator from "../../../../utils/translator";
import Select from "../../../../components/Select/Select";
import { shouldQuoteExpressOnAddressChange } from "../utils/expressAddressChange";
import { getAddressFormTitleKey } from "../utils/rxSelfPickupSubscription";
import { useEffect, useRef, useMemo } from "react";
import { BsTelephone } from "react-icons/bs";
import { LuBuilding2, LuHome, LuBriefcase, LuBedDouble, LuMapPin } from "react-icons/lu";
import { validateCompany, validateName, validatePhone, validatePhoneDE, validatePostalCode, validateStreetAndNumber } from "../../../../utils/fieldsValidator";
import { MainTracker } from "../../../../PixelTrackers/MainTracker";
import ToggleRadioButton from "../../../../components/ToggleRadioButton/ToggleRadioButton";
import { useShippingPageContext } from "../providers/ShippingPageProvider";
import { useCartContext } from "../../../../customHooks/useCart";
import { useAppContext } from "../../../../providers/AppProvider";
import ByteplantAddressValidatorModal from "../../../../components/Modals/ByteplantAddressValidatorModal";
import PortaledModal from "../../../../components/Modals/PortaledModal";
import SplitDelivery from "../../../../components/ExpressDelivery/SplitDelivery/SplitDelivery";
import GoogleApiRestrictedInputField from "../../../../components/CredentialsInput/GoogleApiRestrictedInputField";
import RestrictedInputField from "../../../../components/CredentialsInput/RestrictedInputField";
import { CANNABIS_ID } from "../../../../utils/consts";
import { isAddressPhoneValid } from "../components/InvalidPhonePopup";
const FORM_ID = "delivery-address-form";

const GERMANY_COUNTRY_ID = "DE";

type Props = {
  defaultValues?: Partial<DeliveryAddressFormType>;
  showToggle?: boolean;
  isGoogleAPILoaded?: boolean;
  isAddressCardMode?: boolean;
  orangeToggle?: boolean;
}



const DeliveryAddressForm: React.FC<Props> = ({ defaultValues, showToggle = true, isGoogleAPILoaded = true, isAddressCardMode = false, orangeToggle = false }) => {

  const { language, country, setSelectedFormID, countryCurrenciesByID, setIsSubmitButtonDisabled, handleShippingSubmit, handleDeliveryOptionChange, getExpressQuote, expressDeliveryState, setExpressDeliveryState, byteplantAddressValidatorState, setByteplantAddressValidatorState, identityCheckState, setIdentityCheckState, updateCartZipcode, expressStockCode, isCannabisCatalog, customerAddress, setInvalidPhoneAddress } = useShippingPageContext();
  const {cartCurrency, setCartCurrency, getCart} = useCartContext();
  const {setStickyNotification} = useAppContext();
  const cart = getCart();


  // Refs to prevent unnecessary calls to Express Quote API
  const prevZipRef = useRef(defaultValues?.zip || "");
  const prevAddressRef = useRef(defaultValues?.address || "");
  const prevCityRef = useRef(defaultValues?.city || "");
  const prevExpressStockCodeRef = useRef(expressStockCode || null);
  const lastTrackedInvalidPhoneRef = useRef("");

  const isSelfPickup = cart.selected_service_option === "self_pickup";
  const isOnlyPrescription = cart.selected_service_option === "only_prescription";

  //Init the formID
  useEffect(() => {
    setSelectedFormID(FORM_ID);
    if (defaultValues && !defaultValues.country) {
      defaultValues.country = cartCurrency.countryID;
    }
  }, [defaultValues])

  // update the delivery price when the address changes
  useEffect(() => {
    if (shouldQuoteExpressOnAddressChange({ isAvailable: expressDeliveryState.isAvailable, isCannabisCatalog }) && (expressDeliveryState.deliveryPriceLoading || prevExpressStockCodeRef.current !== expressStockCode)) {
      getExpressQuote(watch());
      prevExpressStockCodeRef.current = expressStockCode;
    }
  }, [expressDeliveryState.deliveryPriceLoading, expressStockCode])

  const locationTypeOptions = [
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_apartment") || "Apartment", code: "apartment", icon: <LuBuilding2 size={14} /> },
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_house") || "House", code: "house", icon: <LuHome size={14} /> },
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_work") || "Office", code: "office", icon: <LuBriefcase size={14} /> },
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_hotel") || "Hotel", code: "hotel", icon: <LuBedDouble size={14} /> },
    { value: translator.translate(language, "shipping", "ShippingForm/address_sub_type_other") || "Other", code: "other", icon: <LuMapPin size={14} /> },
  ];

  //Country dropdown options
  const countryDropdownOptions = useMemo(() => { 
    return Object.values(countryCurrenciesByID).map((currency) => {
      return {
        value: translator.translate(language, "shipping", `ShippingForm/country_name/${currency.countryID?.toLowerCase()}`),
        code: currency.countryID
      }
    })
  }, [countryCurrenciesByID])



  // Zod Schema 
  const deliveryAddressSchema = z.object({
    // First name field
    first_name: z
      .string()
      .refine(
        (value: string) => {
          const { isValid } = validateName(value, language);
          return isValid;
        },
        (value) => ({
          message: validateName(value, language).errorMsg
        })
      ),

    // Last name field
    last_name: z
      .string()
      .refine(
        (value: string) => {
          const { isValid } = validateName(value, language);
          return isValid;
        },
        (value) => ({
          message: validateName(value, language).errorMsg
        })
      ),

    // Zip field
    zip: z
      .string()
      .refine(
        (postalCode: string) => {
          const { isValid } = validatePostalCode(postalCode, language, cartCurrency.countryID);
          return isValid;
        },
        (value) => ({
          message: validatePostalCode(value, language, cartCurrency.countryID).errorMsg
        })
      ),

    // City field 
    city: z
      .string()
      .refine(
        (value: string) => {
          const { isValid } = validateName(value, language);
          return isValid;
        },
        (value) => ({
          message: validateName(value, language).errorMsg
        })
      ),

    // Address field 
    address: z
      .string()
      .refine(
        (value: string) => {
          const { isValid } = validateStreetAndNumber(value, language);
          return isValid;
        },
        (value) => ({
          message: validateStreetAndNumber(value, language).errorMsg
        })
      ),

    // Country Field  
    country: z.string(),


    // Company Field
    company: z
      .string()
      .refine(
        (value: string) => {
          const { isValid } = validateCompany(value, language);
          return isValid;
        },
        (value) => ({
          message: validateCompany(value, language).errorMsg
        })
      ),


    // Phone Field
    phone: z
      .string()
      .refine(
        (value: string) => {
          if (cartCurrency.countryID?.toLowerCase() === 'de') {
            const { isValid } = validatePhoneDE(value, language);
            return isValid;
          }
          const { isValid } = validatePhone(value, language);
          return isValid;
        },
        (value) => {
          if (cartCurrency.countryID?.toLowerCase() === 'de') {
            return { message: validatePhoneDE(value, language).errorMsg };
          }
          return { message: validatePhone(value, language).errorMsg };
        }
      ),

    // Toggle for Billing Address:
    useAddressAsBilling: z.boolean(),

    // For billing_first_name:
    billing_first_name: z.string().optional(),

    // For billing_last_name:
    billing_last_name: z.string().optional(),

    // For billing_zip:
    billing_zip: z.string().optional(),

    // For billing_city:
    billing_city: z.string().optional(),

    // For billing_address:
    billing_address: z.string().optional(),
   
    // For billing_company:
    billing_company: z.string().optional(),

    // Location type for delivery address
    address_sub_type: z.string(),

  }).superRefine((schema, ctx) => {
    if (!schema.useAddressAsBilling) { 
      // Toggle off, need to also check billing fields
      let { isValid, errorMsg } = validateName(schema.billing_first_name, language);
      if (!isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["billing_first_name"],
          message: errorMsg,
        });
      }
  
      ({ isValid, errorMsg } = validateName(schema.billing_last_name, language)); 
      if (!isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["billing_last_name"],
          message: errorMsg,
        });
      }
  
      ({ isValid, errorMsg } = validatePostalCode(schema.billing_zip, language, cartCurrency.countryID));
      if (!isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["billing_zip"],
          message: errorMsg,
        });
      }
  
      ({ isValid, errorMsg } = validateName(schema.billing_city, language));
      if (!isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["billing_city"],
          message: errorMsg,
        });
      }
  
      ({ isValid, errorMsg } = validateStreetAndNumber(schema.billing_address, language));
      if (!isValid) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["billing_address"],
          message: errorMsg,
        });
      }
    }
  });
  


// Form hook 
const { handleSubmit, control, formState: { errors }, watch, setValue } = useForm<DeliveryAddressFormType>({
  resolver: zodResolver(deliveryAddressSchema),
  mode: "onTouched",
  defaultValues: {
    first_name: defaultValues?.first_name || "",
    last_name: defaultValues?.last_name || "",
    zip: defaultValues?.zip || "",
    city: defaultValues?.city || "",
    address: defaultValues?.address || "",
    country: cartCurrency.countryID,
    company: defaultValues?.company || "",
    phone: defaultValues?.phone || "",
    useAddressAsBilling: true,
    address_sub_type: defaultValues?.address_sub_type || "apartment",
    billing_first_name: defaultValues?.first_name || "",
    billing_last_name: defaultValues?.last_name || "",
    billing_zip: "",
    billing_city: "",
    billing_address: "",
    billing_company: ""
  },
});

//Watch the toggle in order to render billing form (or not)
const useAddressAsBillingValue = watch("useAddressAsBilling");


//Scroll to the first input field with validation error
const onValidationFailed = () => {
  const firstErrorField = Object.keys(errors)[0];
  if (firstErrorField) {
    const errorElement = document.getElementById(`${FORM_ID}-${firstErrorField.replace(/_/g, '-')}`);
    if (errorElement) {
      errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      (errorElement as HTMLElement).focus();
    }
  }
};


// Handle customer using suggestion after the modal pops up
const handleCustomerUsingSuggestion = (byteplantSuggestion:ByteplantApiResponse) => {
  // Check if suggestion has all required fields
  const hasSuggestion = byteplantSuggestion.street && byteplantSuggestion.streetnumber && byteplantSuggestion.city && byteplantSuggestion.postalcode;
  
  // No suggestion available
  if(!hasSuggestion){
    setByteplantAddressValidatorState(current => ({
      showModal: false,
    shouldValidate: true,
    responseData:  null,
    customerUsedSuggestion: false 
  }))
  }
  else{
    // Suggestion available
    let customerUsedSuggestion = true;
    const suggestionValues: Partial<DeliveryAddressFormType> = {};

    setByteplantAddressValidatorState((prevState) => ({
      ...prevState,
      showModal: false,
      customerUsedSuggestion: true
    }));
    suggestionValues.address = `${byteplantSuggestion.street} ${byteplantSuggestion.streetnumber}`;
    suggestionValues.city = byteplantSuggestion.city;
    suggestionValues.zip = byteplantSuggestion.postalcode;

    setValue("address", suggestionValues.address);
    setValue("city", suggestionValues.city);
    setValue("zip", suggestionValues.zip);

    handleSubmit((formData) => {handleShippingSubmit({...formData, ...suggestionValues}, customerUsedSuggestion, expressDeliveryState.isSelected)}, onValidationFailed)()
  }
}

// split = 2 orders: dhl + express
// dhl = 1 order: dhl only!
const onExpressSplitPopupSubmit = async (deliveryOption: "split" | "dhl") => {
  setExpressDeliveryState((prevState) => ({
    ...prevState,
    showSplitDeliveryPopup: false,
  }));
  if (deliveryOption === "split") {
    handleShippingSubmit(watch(), undefined, true);
  }
  else {
    await handleDeliveryOptionChange("regular");
    handleShippingSubmit(watch(), undefined, false);
  }
}

const handleSubmitButtonClick = (formData: DeliveryAddressFormType) => {
  const productsWIthoutCannabisID = cart.products.filter(p => p.type !== CANNABIS_ID);
  if (identityCheckState.showWidget && !identityCheckState.isPrecautionToggleEnabled) {
    setIdentityCheckState((prevState) => ({
      ...prevState,
      precautionLevel: "error"
    }))
    if (country?.toUpperCase() === GERMANY_COUNTRY_ID && !cart.selected_service_option) {
      setStickyNotification({
        show: true,
        title: translator.translate(language, "shipping", "validation/select_delivery_option_title"),
        content: translator.translate(language, "shipping", "validation/select_delivery_option_content"),
        level: "error",
        showInfoIcon: true,
        autoFade: true
      });
      return;
    }
  
    return
  }
  // Check if user has chosen a delivery option (only for Germany)
  // Skip validation if there's only one option available (it will be auto-selected)
  if (country?.toUpperCase() === GERMANY_COUNTRY_ID && !cart.selected_service_option) {
    setStickyNotification({
      show: true,
      title: translator.translate(language, "shipping", "validation/select_delivery_option_title"),
      content: translator.translate(language, "shipping", "validation/select_delivery_option_content"),
      level: "error",
      showInfoIcon: true,
      autoFade: true
    });
    return;
  }

  setIsSubmitButtonDisabled(true);
  // finally: never leave the button stuck disabled if anything below throws.
  try {
    // If Express delivery is selected, check if order needs to be split
    if (expressDeliveryState.isSelected) {
      // An empty map means express was cancelled (e.g. the unavailable-fallback) — nothing to split.
      const expressProductIDs = Object.values(expressDeliveryState.shippingMethodToProductIDs)[0];
      // If the order has express only products and products without express shipping, we need to split the order
      if (expressProductIDs && productsWIthoutCannabisID.length > 1 && expressProductIDs.length < productsWIthoutCannabisID.length) {

        setExpressDeliveryState((prevState) => ({
          ...prevState,
          showSplitDeliveryPopup: true,
        }));
      }
      else if (expressProductIDs) {
        handleShippingSubmit(formData, undefined, true);
      }
      else {
        handleShippingSubmit(formData);
      }
    }
    else {
      handleShippingSubmit(formData);
    }
  } finally {
    setIsSubmitButtonDisabled(false);
  }
}

return (

  <>
    {/* Small Title — hidden in card mode since DeliveryAddressCard already shows the label */}
    {!isAddressCardMode && (
      <span id={`${FORM_ID}-title`} className="font-semibold text-[#98a0af] font-roboto text-[13px] my-[20px] block">
        {translator.translate(language, "shipping", getAddressFormTitleKey({ isSelfPickup, isOnlyPrescription, isCannabisCatalog, isSubscription: Boolean(cart.subscription_plan_id) }))}
      </span>
    )}

      {/* Address Validator (Byteplant) Modal  */}
      <PortaledModal
        shouldBackgroundClickHideModal={false}
        shouldShowModal={byteplantAddressValidatorState.showModal}
        backgroundColor="rgba(13, 44, 84, 0.36)"
        hideModal={() => setByteplantAddressValidatorState({
          showModal: false,
          shouldValidate: false,
          responseData: null,
          customerUsedSuggestion: false
        })}
      >
      <ByteplantAddressValidatorModal
        responseData={byteplantAddressValidatorState.responseData}
        language={language}
        handleCustomerUsingSuggestion={handleCustomerUsingSuggestion}
        handleCustomerIgnoreSuggestion={handleSubmit((formData) => {handleShippingSubmit(formData, false)}, onValidationFailed)}
        onClose={() => setByteplantAddressValidatorState({ showModal: false, shouldValidate: false, responseData: null, customerUsedSuggestion: false })}
        isExpress={expressDeliveryState.isSelected}
      /> 
      </PortaledModal>

      <SplitDelivery language={language} expressDeliveryState={expressDeliveryState} setExpressDeliveryState={setExpressDeliveryState} onSelectDeliveryOption={onExpressSplitPopupSubmit} products={cart.products} />

    <form 
      id={FORM_ID} 
      onSubmit={(e) => {
        if (isAddressCardMode && !isAddressPhoneValid(customerAddress?.regular, language || "")) {
          e.preventDefault();
          setInvalidPhoneAddress(customerAddress?.regular);
          return;
        }
        // Card mode hides the phone Controller — RHF still validates `phone` with Zod on submit, but the value only
        // reflected initial defaultValues (same address id ⇒ no remount). Align with saved address before validation.
        if (isAddressCardMode && customerAddress?.regular) {
          setValue("phone", customerAddress.regular.phone ?? "", { shouldDirty: false });
        }
        handleSubmit(async (formData) => {
          handleSubmitButtonClick(formData);
        }, onValidationFailed)(e);
      }}
      className="grid grid-cols-1 md:grid-cols-2 gap-4 scroll-mt-[100px]">

      {/* Address fields — hidden in card mode */}
      {!isAddressCardMode && <>

      {/* Location Type */}
      <Controller
        name="address_sub_type"
        control={control}
        render={({ field }) => (
          <div className="md:col-span-2">
            <Select
              id="delivery-address-form-address-sub-type"
              initialValue={locationTypeOptions.find(o => o.code === field.value) || locationTypeOptions[0]}
              options={locationTypeOptions as any}
              onChangeCallback={({ option }: any) => field.onChange(option.code)}
              questionnaireValidationObject={{}}
              errorMode={false}
              style={{
                border: "1px solid var(--sub-gray)",
                height: "56px",
                width: "100%",
                borderRadius: "6px",
                margin: "0px",
                backgroundColor: "transparent",
              }}
              label={translator.translate(language, "shipping", "ShippingForm/address_sub_type_label") || "Type location"}
            />
          </div>
        )}
      />

      {/* First Name  */}
      <Controller
        name="first_name"
        control={control}
        render={({ field, fieldState }) => (
          <RestrictedInputField
            id="delivery-address-form-first-name"
            ignoreTouched={true}
            value={field.value}
            onChange={(value: string) => {
              field.onChange(value);
            }}
            placeholder={translator.translate(language, "shipping", "ShippingForm/first_name")}
            mandatory={true}
            language={language}
            onBlur={field.onBlur}
            errorMsg={fieldState?.error?.message} // Bind error message from hook

          />
        )}
      />

      {/* Last Name  */}
      <Controller
        name="last_name"
        control={control}
        render={({ field, fieldState }) => (
          <RestrictedInputField
            id="delivery-address-form-last-name"
            ignoreTouched={true}
            value={field.value}
            onChange={(value: string) => {
              field.onChange(value);
            }}
            placeholder={translator.translate(language, "shipping", "ShippingForm/surname")}
            mandatory={true}
            errorMsg={fieldState.error?.message}
            language={language}
            onBlur={field.onBlur}
          />
        )}
      />

      {/* Zip  */}
      <Controller
        name="zip"
        control={control}
        render={({ field, fieldState }) => (
            <RestrictedInputField
              id="delivery-address-form-zip"
              ignoreTouched={true}
              value={field.value}
              onChange={(value: string) => {
                field.onChange(value);
                // Invalidating the byteplant address validator
                setByteplantAddressValidatorState((current) => ({...current, responseData: null}))
              }}
              placeholder={translator.translate(language, "shipping", "ShippingForm/postal_code")}
              mandatory={true}
              language={language}
              onBlur={() => {
                field.onBlur();
                // Only update the cart if the zipcode actually changed and correct format
                if (shouldQuoteExpressOnAddressChange({ isAvailable: expressDeliveryState.isAvailable, isCannabisCatalog }) && field.value !== prevZipRef.current && field.value.length == 5 && /^\d{5}$/.test(field.value)) {
                  updateCartZipcode(field.value);
                  prevZipRef.current = field.value;
                }
              }}
              errorMsg={fieldState.error?.message}
            />
          )
        }
      />


      {/* City  */}
      <Controller
        name="city"
        control={control}
        render={({ field, fieldState }) => (
          <GoogleApiRestrictedInputField
            id="delivery-address-form-city"
            value={field.value}
            onChange={(value: any) => {
              if (Array.isArray(value.split(","))) {
                value = value.split(",")[0];
              }
              field.onChange(value); // Update the form field value

              // Invalidating the byteplant address validator
              setByteplantAddressValidatorState((current) => ({...current, responseData: null}))
            }}
            placeholder={translator.translate(
              language,
              "shipping",
              "ShippingForm/location"
            )}
            mandatory={true}
            country={cartCurrency.countryID}
            errorMsg={fieldState?.error?.message} // Bind error message from hook
            onBlur={() => {
                field.onBlur();
                if (shouldQuoteExpressOnAddressChange({ isAvailable: expressDeliveryState.isAvailable, isCannabisCatalog }) && field.value !== prevCityRef.current) {
                  setExpressDeliveryState((prevState) => ({
                    ...prevState,
                    deliveryPriceLoading: true,
                  }));
                  prevCityRef.current = field.value;
                }
              }
            }
            addressType={"(cities)"}
            isGoogleApiLoaded={isGoogleAPILoaded}
            restrictedChars={['@', '!']}
          />
        )}
      />



      {/* Shipping address  */}
      <div id="address-input-container" className="md:col-span-2">
        <Controller
          name="address"
          control={control}
          render={({ field, fieldState }) => (
            <GoogleApiRestrictedInputField
              id="delivery-address-form-address"
              value={field.value}
              onChange={(value: any) => {
                if (Array.isArray(value.split(","))) {
                  value = value.split(",")[0];
                }
                field.onChange(value); // Update the form field value
              // Invalidating the byteplant address validator
              setByteplantAddressValidatorState((current) => ({...current, responseData: null}))
              }}
              placeholder={translator.translate(
                language,
                "shipping",
                "ShippingForm/street_and_house"
              )}
              mandatory={true}
              country={cartCurrency.countryID}
              errorMsg={fieldState?.error?.message} // Bind error message from hook
              onBlur={() => {
                  field.onBlur();
                  if (shouldQuoteExpressOnAddressChange({ isAvailable: expressDeliveryState.isAvailable, isCannabisCatalog }) && field.value !== prevAddressRef.current) {
                    setExpressDeliveryState((prevState) => ({
                      ...prevState,
                      deliveryPriceLoading: true,
                    }));
                    prevAddressRef.current = field.value;
                  }
                }
              }
              addressType={"address"}
              isGoogleApiLoaded={isGoogleAPILoaded}
            />
          )}
        />
      </div>


      {/* Country */}
      <Controller
        name="country"
        control={control}
        render={({ field }) => (
          <Select
            id="delivery-addres-form-country"
            initialValue={{
              value: translator.translate(language, "shipping", `ShippingForm/country_name/${field.value?.toLowerCase()}`),
              code: field.value,
            }}
            options={countryDropdownOptions as any}
            onChangeCallback={({ option }) => {

              const countryID = option.code;

              field.onChange(countryID); // Updates the form state
              setCartCurrency(countryCurrenciesByID[countryID?.toLowerCase()]); //Update visual currency

              // Invalidating the byteplant address validator
              setByteplantAddressValidatorState((current) => ({...current, responseData: null}))
            }}
            questionnaireValidationObject={{}}
            errorMode={false}
            style={{
              border: "1px solid var(--sub-gray)",
              height: "56px",
              width: "100%",
              borderRadius: "6px",
              margin: "15px 0px",
              backgroundColor: "transparent",
            }}
            label={translator.translate(language, "shipping", "ShippingForm/country")}
            disabled={true}
          />
        )}
      />


      {/* Company (Firm)  */}
      <Controller
        name="company"
        control={control}
        render={({ field }) => (
          <RestrictedInputField
            id="delivery-address-form-company"
            ignoreTouched={true}
            value={field.value}
            onChange={(value: string) => {
              field.onChange(value);
            }}
            placeholder={translator.translate(language, "shipping", "ShippingForm/firm_name")}
            mandatory={false}
            language={language}
            onBlur={field.onBlur}
          />
        )}
      />

      </> /* end !isAddressCardMode */}

      {/* Billing Address Toggle */}
      {
        showToggle &&

        <>
          <Controller
            name="useAddressAsBilling"
            control={control}
            render={({ field }) => (
              <div id="address-as-billing" className="flex flex-row justify-center gap-3 md:gap-2 mt-4 mb-2 items-center md:col-span-2">
                <ToggleRadioButton
                  id="toggle_address_as_billing"
                  isToggleEnabled={field.value}
                  updateToggle={(value: boolean) => field.onChange(value)}
                  orange={orangeToggle}
                />
                <p className="address-as-billing-text">
                  {translator.translate(
                    language,
                    "shipping",
                    "ShippingForm/address_as_billing_text"
                  )}
                </p>
              </div>
            )}
          />

        </>
      }


      {/* Billing Form */}
      {
        !useAddressAsBillingValue && (
          <>
            {/* Billing Address Title */}
            <span id={`${FORM_ID}-billing-title`} className="font-semibold text-[#98a0af] font-roboto text-[13px] my-[5px] block md:col-span-2">
              {translator.translate(language, "shipping", "layout/billing_address")}
            </span>

            {/* Billing First Name  */}
            <Controller
              name="billing_first_name"
              control={control}
              render={({ field, fieldState }) => (
                <RestrictedInputField
                  id="delivery-address-form-billing-last-name"
                  ignoreTouched={true}
                  value={field.value}
                  onChange={(value: string) => { field.onChange(value) }}
                  placeholder={translator.translate(language, "shipping", "ShippingForm/first_name")}
                  mandatory={true}
                  errorMsg={errors.billing_first_name?.message}
                  language={language}
                  onBlur={field.onBlur}
                />
              )}
            />

            {/* Billing Last Name  */}
            <Controller
              name="billing_last_name"
              control={control}
              render={({ field }) => (
                <RestrictedInputField
                  id="delivery-address-form-billing-first-name"
                  ignoreTouched={true}
                  value={field.value}
                  onChange={(value: string) => { field.onChange(value) }}
                  placeholder={translator.translate(language, "shipping", "ShippingForm/last_name")}
                  mandatory={true}
                  errorMsg={errors.billing_last_name?.message}
                  language={language}
                  onBlur={field.onBlur}
                />
              )}
            />

            {/* Billing Zip */}
            <Controller
              name="billing_zip"
              control={control}
              render={({ field }) => (
                <RestrictedInputField
                  id="delivery-address-form-billing-zip"
                  ignoreTouched={true}
                  value={field.value}
                  onChange={(value: string) => {
                    field.onChange(value);
                  }}
                  placeholder={translator.translate(language, "shipping", "ShippingForm/postal_code")}
                  mandatory={true}
                  errorMsg={errors.billing_zip?.message}
                  language={language}
                  onBlur={field.onBlur}
                />
              )}
            />

            {/* Billing City */}
            <Controller
              name="billing_city"
              control={control}
              render={({ field, fieldState }) => (
                <GoogleApiRestrictedInputField
                  id="delivery-address-form-billing-city"
                  value={field.value}
                  onChange={(value: any) => {
                    if (Array.isArray(value.split(","))) {
                      value = value.split(",")[0];
                    }
                    field.onChange(value); // Update the form field value
                  }}
                  placeholder={translator.translate(
                    language,
                    "shipping",
                    "ShippingForm/location"
                  )}
                  mandatory={true}
                  country={cartCurrency.countryID}
                  errorMsg={fieldState?.error?.message} // Bind error message from hook
                  onBlur={field.onBlur} // Trigger onBlur to update touched state
                  addressType={"(cities)"}
                  isGoogleApiLoaded={isGoogleAPILoaded}
                />
              )}
            />

            {/* Billing shipping address */}
            <div id="billing-address-input-container" className="md:col-span-2">
              <Controller
                name="billing_address"
                control={control}
                render={({ field, fieldState }) => (
                  <GoogleApiRestrictedInputField
                    id="delivery-address-form-billing-address"
                    value={field.value}
                    onChange={(value: any) => {
                      if (Array.isArray(value.split(","))) {
                        value = value.split(",")[0];
                      }
                      field.onChange(value); // Update the form field value
                    }}
                    placeholder={translator.translate(
                      language,
                      "shipping",
                      "ShippingForm/street_and_house"
                    )}
                    mandatory={true}
                    country={cartCurrency.countryID}
                    errorMsg={fieldState?.error?.message} // Bind error message from hook
                    onBlur={field.onBlur} // Trigger onBlur to update touched state
                    addressType={"address"}
                    isGoogleApiLoaded={isGoogleAPILoaded}
                  />
                )}
              />
            </div>

            {/* Billing Country */}
            <div id="billing-country-container" className="md:col-span-2">
              <Controller
                name="country"
                control={control}
                render={({ field, fieldState }) => (
                  <Select
                    id="delivery-addres-form-billing-country"
                    initialValue={{
                      value: translator.translate(language, "shipping", `ShippingForm/country_name/${field.value?.toLowerCase()}`),
                      code: field.value,
                    }}
                    options={countryDropdownOptions as any}
                    onChangeCallback={({ option }) => {}}
                    questionnaireValidationObject={{}}
                    errorMode={false}
                    style={{
                      border: "1px solid var(--sub-gray)",
                      height: "56px",
                      width: "100%",
                      borderRadius: "6px",
                      margin: "15px 0px",
                      backgroundColor: "transparent",
                    }}
                    label={translator.translate(language, "shipping", "ShippingForm/country")}
                    disabled={true}
                  />
                )}
              />
            </div>
          </>
        )
      }

      {/* Phone — hidden in card mode since address already has phone */}
      {!isAddressCardMode && <Controller
        name="phone"
        control={control}
        render={({ field, fieldState }) => (
          <div id="phone-field-container" className="text-[#98a0af] flex flex-col md:flex-row gap-4 md:col-span-2">
            {/* Icon and text */}
            <div id="phone-icon-and-text" className="flex gap-3 items-center w-full">
              <BsTelephone className="w-[25px] h-[25px] flex-shrink-0" />
              <p id="phone-icon-text" className="text-[13px] font-roboto">
                {translator.translate(language, "shipping", "ShippingForm/phone_icon_text")}
              </p>
            </div>

            <RestrictedInputField
              id="delivery-address-form-phone"
              ignoreTouched={true}
              value={field.value}
              onChange={(value: string) => {
                field.onChange(value);
              }}
              placeholder={translator.translate(language, "shipping", "ShippingForm/telephone")}
              mandatory={true}
              errorMsg={fieldState?.error?.message}
              language={language}
              onBlur={() => {
                if (cartCurrency.countryID?.toLowerCase() === 'de' && field.value) {
                  const result = validatePhoneDE(field.value, language);
                  if (result.isValid && result.e164 && result.e164 !== field.value) {
                    field.onChange(result.e164);
                  }
                  if (!result.isValid && field.value !== lastTrackedInvalidPhoneRef.current) {
                    lastTrackedInvalidPhoneRef.current = field.value;
                    MainTracker.track("custom", "Invalid phone number", {
                      "Invalid phone number": field.value
                    });
                  }
                }
                field.onBlur();
              }}
            />
          </div>
        )}
      />}

      {/* The Parent (shipping page) is handling the submit */}
    </form>
  </>
);
};

export default DeliveryAddressForm;